import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { MessagesIcon } from "@/components/chat/MessagesIcon";
import { NotificationBell } from "@/components/NotificationBell";
import { ProfileMenu } from "@/components/ProfileMenu";

const LINKS = [
  { label: "NEW", action: "all" },
  { label: "SHOP", action: "fashion" },
  { label: "BEAUTY", action: "beauty" },
  { label: "ABOUT", action: "about" },
];

const useIsMobile = () => {
  const q = "(max-width: 639px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
};

export const NavBar = ({ onNav: onNavProp, search: searchProp, onSearch: onSearchProp }) => {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const onNav = onNavProp || ((action) => navigate("/", { state: { nav: action } }));
  // Pages without their own search send the query to the home page
  const search = onSearchProp ? searchProp : localSearch;
  const onSearch = onSearchProp || setLocalSearch;
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open, isMobile]);

  const toggleSearch = () => {
    if (open) onSearch("");
    setOpen(!open);
  };

  const searchInput = (
    <form className="w-full" onSubmit={(e) => { e.preventDefault(); if (!onSearchProp && localSearch.trim()) navigate("/", { state: { search: localSearch.trim() } }); inputRef.current?.blur(); }}>
      <input
        ref={inputRef}
        data-testid="nav-search-input"
        type="search"
        enterKeyHint="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search the edit…"
        className="w-full bg-transparent border-b border-[#C5A059]/60 text-sm py-1.5 px-1 outline-none placeholder:text-white/35 focus:border-[#C5A059]"
      />
    </form>
  );

  return (
    <header data-testid="top-navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0F0F0F]/90 border-b border-[#C5A059]/20 text-white pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between gap-2 sm:gap-4 px-3 min-[380px]:px-4 sm:px-8 lg:px-12 h-16 lg:h-20">
        <Link to="/" data-testid="nav-logo" className="font-display text-sm min-[350px]:text-base min-[380px]:text-lg sm:text-2xl tracking-[0.08em] min-[350px]:tracking-[0.12em] min-[380px]:tracking-[0.18em] uppercase whitespace-nowrap">
          Lamka <span className="text-[#C5A059]">Online</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => onNav(l.action)}
              className="nav-link text-xs tracking-[0.3em] text-white/80 hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-0.5 sm:gap-2">
          <div className={`hidden sm:flex items-center overflow-hidden transition-[width,opacity] duration-300 ${open ? "w-56 opacity-100" : "w-0 opacity-0"}`}>
            {!isMobile && searchInput}
          </div>
          <button
            data-testid="nav-search-button"
            aria-label="Search"
            onClick={toggleSearch}
            className="p-2 min-[350px]:p-2.5 text-white/80 hover:text-[#C5A059] transition-colors"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Search className="h-5 w-5" strokeWidth={1.5} />}
          </button>
          <MessagesIcon />
          <NotificationBell />
          <ProfileMenu />
        </div>
      </div>
      {isMobile && open ? (
        <div className="md:hidden px-4 pb-3">{searchInput}</div>
      ) : (
        <nav className="md:hidden flex justify-between px-2 pb-1">
          {LINKS.map((l) => (
            <button key={l.label} data-testid={`nav-mobile-link-${l.label.toLowerCase()}`} onClick={() => onNav(l.action)} className="px-2 py-3 text-[10px] tracking-[0.25em] text-white/70 active:text-[#C5A059] hover:text-[#C5A059]">
              {l.label}
            </button>
          ))}
        </nav>
      )}
    </header>
  );
};
